import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import services from 'api/services';
import * as passwordActions from 'global/actions/passwordActions';
import { colors } from 'global/colors';
import { passwordHasKeyword } from 'functions/stringValidation';
import { userRoles, passwordRegex } from 'util/appConstants';
import { Images } from 'util/images';
import { useAppContext } from 'hooks/useContext';
import { useActions } from 'hooks/useActions';
import apiErrors from 'api/apiErrors';
import { Wrapper, Container, Input, Header, Button, InfoText, Form } from '../../components';
import CurvedContainer from '../../components/CurvedContainer';
import { BottomContainer } from './styles';
import * as strings from './strings';

const { AuthService } = services;

export default React.memo(({ match }) => {
  const { session } = match.params;

  const { language } = useAppContext();

  const { loading, error, success } = useSelector((state) => state.managePassword);
  const { clearError, clearState, submitPassword } = useActions(passwordActions);

  const [validPassword, setValidPassword] = useState(false);
  const [invalidKeywordsUsed, setInvalidKeywordUsed] = useState(false);
  const [name, setName] = useState('');
  const [communityName, setCommunityName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [nameIsRequired, setNameIsRequired] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  useEffect(() => {
    const getAccountData = async () => {
      try {
        let { user } = await AuthService.getAccount(session);
        if (!user?.role) {
          return setErrorModalMessage('User not found');
        }
        if (user.role === userRoles.CANDIDATE && !user.coderCommunityProfile?.name) {
          setNameIsRequired(true);
        }
        setName(user.name || '');
        setEmail(user.email || '');
        setCommunityName(user.coderCommunityProfile?.name || '');
      } catch (err) {
        if (err.name === 'RESOURCE_NOT_FOUND_ERROR') {
          setErrorModalMessage('The link is invalid. Please make sure you copied the full link.');
        } else {
          setErrorModalMessage(err.message);
        }
      }
    };
    getAccountData();
  }, [session, nameIsRequired]);

  useEffect(() => {
    return () => clearState();
  }, [clearState]);

  useEffect(() => {
    setValidPassword(passwordRegex.test(password));
    setInvalidKeywordUsed(passwordHasKeyword(password, email, name, communityName));
  }, [password, communityName, name, email]);

  const renderErrorModal = () => (
    <Modal className="detailsPopUp" isOpen={!!errorModalMessage}>
      <ModalHeader cssModule={{ 'modal-title': 'w-100 ' }}>Error</ModalHeader>
      <ModalBody>
        <span
          style={{
            width: '100%',
            fontSize: 16,
            color: '#616161',
            paddingTop: '20px',
          }}
        >
          {errorModalMessage}
        </span>
      </ModalBody>
    </Modal>
  );

  return (
    <Wrapper>
      {renderErrorModal()}
      <CurvedContainer bottom image={Images.art} height={110} />
      <Container color={colors.lightGray} half column>
        <Header>{strings.header[language]}</Header>
        <Form onChange={() => error && clearError()} onSubmit={() => submitPassword(password, communityName, session)}>
          {nameIsRequired && (
            <Input
              autoFocus
              type="text"
              value={communityName}
              placeholder={strings.namePlaceholder[language]}
              onChange={(e) => setCommunityName(e.target.value)}
              onBlur={(e) => {
                setCommunityName(e.target.value.trim());
              }}
            />
          )}
          <Input
            autoFocus
            type="password"
            value={password}
            placeholder={strings.passwordPlaceholder[language]}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Input
            type="password"
            value={repeatPassword}
            placeholder={strings.repeatPasswordPlaceholder[language]}
            onChange={(e) => setRepeatPassword(e.target.value)}
          />
          <Button
            primary
            type="submit"
            disabled={
              !password ||
              !repeatPassword ||
              error ||
              !validPassword ||
              password !== repeatPassword ||
              invalidKeywordsUsed ||
              (nameIsRequired && !communityName)
            }
          >
            {strings.header[language]}
          </Button>
        </Form>
        <BottomContainer>
          {repeatPassword && password !== repeatPassword && (
            <InfoText error={password !== repeatPassword}>Passwords don't match</InfoText>
          )}
          <InfoText medium error={error} success={success}>
            {loading ? strings.loadingLabel[language] : ''}
            {apiErrors?.[error?.name]?.[language] ?? error}
            {success ? strings.resetSuccessLabel[language] : ''}
          </InfoText>
        </BottomContainer>
        <InfoText medium>Instructions:</InfoText>
        <InfoText success={validPassword ? true : false}>
          1. Password must be at least 10 characters long, contain a lowercase letter, one uppercase letter, and one
          digit.
        </InfoText>
        <InfoText
          error={invalidKeywordsUsed ? true : false}
          success={invalidKeywordsUsed || !password.length ? false : true}
        >
          2. Your Name and E-mail (username and domain of username@domain.com) should not be part of the password.
        </InfoText>
        {nameIsRequired && (
          <InfoText error={!communityName} success={!!communityName?.length}>
            3. A user alias is required.
          </InfoText>
        )}
      </Container>
    </Wrapper>
  );
});
